const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
  validateStatus?: (status: number) => boolean;
}

interface ApiError extends Error {
  response?: { status: number; data: Record<string, unknown> };
}

interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  headers: Headers;
}

class ApiClient {
  private baseUrl: string;
  
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }
  
  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('gcxp_token');
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Send current session language as Accept-Language
    try {
      const sessionData = localStorage.getItem('gcxp_language');
      if (sessionData) {
        headers['Accept-Language'] = sessionData;
      }
    } catch { /* ignore */ }
    return headers;
  }
  
  async request<T = any>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const {
      method = 'GET',
      headers = {},
      body,
      validateStatus = (status) => status >= 200 && status < 300,
    } = config;
    
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    
    const requestHeaders: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...this.getAuthHeader(),
      ...headers,
    };
    
    // Don't set Content-Type for FormData — browser sets it with the boundary
    if (isFormData) {
      delete requestHeaders['Content-Type'];
    }
    
    const fetchConfig: RequestInit = {
      method,
      headers: requestHeaders,
      body: body ? (isFormData ? body as FormData : JSON.stringify(body)) : undefined,
      signal: config.signal,
    };
    
    const response = await fetch(url, fetchConfig);
    
    if (!validateStatus(response.status)) {
      const errorData = await response.json().catch(() => ({}));

      // 403 with INSUFFICIENT_TRUST_LEVEL → trigger verification modal via event
      if (response.status === 403 && errorData?.error === 'INSUFFICIENT_TRUST_LEVEL') {
        window.dispatchEvent(new CustomEvent('gcxp:verification-required', {
          detail: {
            requiredTrustLevel: errorData.requiredTrustLevel,
            requiredTrustLevelName: errorData.requiredTrustLevelName,
          }
        }));
      }

      // 401 means the guest auth handler rejected our token (session expired / checked out /
      // revoked / device gone). The response body is empty, so callers would otherwise show a
      // generic "try again" that can never succeed. Signal the app to re-check the session and
      // route the guest to sign-in. Skipped for /session/validate (it handles 401 itself and would
      // loop) and when no token was sent (nothing to expire — e.g. the sign-in screen itself).
      if (response.status === 401
          && localStorage.getItem('gcxp_token')
          && !endpoint.includes('/session/validate')) {
        window.dispatchEvent(new CustomEvent('gcxp:session-expired'));
      }

      // errorData.error may be an object { code, message } from ExceptionHandlingMiddleware
      const rawError = errorData.error;
      const errorMessage = typeof rawError === 'string'
        ? rawError
        : (rawError as Record<string, unknown>)?.message as string || (errorData as Record<string, unknown>).message as string || 'Request failed';
      const error: ApiError = new Error(errorMessage);
      error.response = { status: response.status, data: errorData as Record<string, unknown> };
      throw error;
    }
    
    const data = response.status !== 204 && response.status !== 304
      ? await response.json()
      : null;
    
    return {
      data,
      status: response.status,
      headers: response.headers,
    };
  }
  
  get<T = any>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>) {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }
  
  post<T = unknown>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method'>) {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  put<T = unknown>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method'>) {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  patch<T = unknown>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method'>) {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  delete<T = unknown>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>) {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
